/**
 * When does a saved recipe deserve a check-in?
 *
 * Imported by BOTH halves: the server decides who gets a reminder, the screen
 * decides which recipes sit in "Waiting on you", and the two must never
 * disagree — a card that says "waiting" for a recipe the server will never
 * remind about, or the reverse, is the bug rule 14 exists for.
 *
 * Derived from the clock, not from a status column (rule 15). Nothing here is
 * "marked stale"; a recipe is stale because nothing has happened to it for a
 * week, and the first thing that does happen ends it.
 */
import { businessDate } from './time'

export const NUDGE_AFTER_DAYS = 7
const DAY_MS = 86_400_000

/** How far ahead a plan may be set. Further than this is a wish, not a plan. */
export const PLAN_HORIZON_DAYS = 60

export interface NudgeFacts {
  status: string
  isSample: boolean
  timesCooked: number
  priorMakes: number | null
  createdAt: number
  updatedAt: number
  /** Last time a cooking session was started for it, if ever. */
  lastSessionAt: number | null
  /** `YYYY-MM-DD` in the business timezone. */
  plannedFor: string | null
  /** The last time she answered a check-in — "ask me next week" sets this. */
  checkedInAt: number | null
  nudgesOff: boolean
}

export type NudgeState =
  /** Has a day in the future (or today). Leave it alone. */
  | { kind: 'planned'; plannedFor: string }
  /** The planned day came and went with no run recorded. */
  | { kind: 'plan_passed'; plannedFor: string }
  /** Nothing has happened to it for NUDGE_AFTER_DAYS. */
  | { kind: 'stale'; idleDays: number }
  | null

/** The most recent sign that someone did something with this recipe. */
export function lastMovement(r: NudgeFacts): number {
  return Math.max(r.createdAt, r.updatedAt, r.lastSessionAt ?? 0, r.checkedInAt ?? 0)
}

export function nudgeState(r: NudgeFacts, tz: string, now: number): NudgeState {
  // Made already, a sample, or she said stop: none of these are waiting on her.
  if (r.nudgesOff || r.isSample) return null
  if (r.status !== 'want_to_try') return null
  if (r.timesCooked > 0 || (r.priorMakes ?? 0) > 0) return null

  if (r.plannedFor) {
    const today = businessDate(tz, now)
    if (r.plannedFor >= today) return { kind: 'planned', plannedFor: r.plannedFor }
    /*
     * The day passed. Answering the check-in moves checkedInAt past the plan,
     * which hands the recipe back to the ordinary weekly clock below rather
     * than asking "did you make it?" every day for ever.
     */
    if (r.checkedInAt === null || businessDate(tz, r.checkedInAt) <= r.plannedFor) {
      return { kind: 'plan_passed', plannedFor: r.plannedFor }
    }
  }

  const idleDays = Math.floor((now - lastMovement(r)) / DAY_MS)
  return idleDays >= NUDGE_AFTER_DAYS ? { kind: 'stale', idleDays } : null
}

/** Is `date` a day a plan may be set for? Checked by the server; offered by the screen. */
export function planDateAllowed(date: string, tz: string, now: number): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(`${date}T12:00:00Z`))) return false
  const today = businessDate(tz, now)
  const last = businessDate(tz, now + PLAN_HORIZON_DAYS * DAY_MS)
  return date >= today && date <= last
}
